/* eslint-disable no-prototype-builtins */
import {Typography} from '@mui/material';
import {IIndexingDetails} from '../../../redux/slices/indexingSlice';
import {t} from 'i18next';
import {
  formatNumberWithComma,
  getUTCDateTimeForDisplay,
} from '../../../utils/utils';
import {formatStatus, IndexesType, indexingStatusKey, shouldShowLastIndexingEvent} from '../model';
import {Status} from '../../../constants/constants';
import AurTooltip from '../../common/aur-tooltip/AurTooltip';
import {ReactComponent as InfoIcon} from '../../../assests/svg/info_icon.svg';
import {AurLightTooltip} from '../../common/aur-light-tooltip/aur-light-tooltip';

interface IIndexHeaderDetails {
  ecaIndexData: IIndexingDetails;
}

const getStatusClassName = (status: string | undefined): string => {
  if (status === indexingStatusKey.inProgress) return 'inprogress-color';
  if (status === indexingStatusKey.complete) return 'completed-color';
  if (status === indexingStatusKey.pending) return 'light-blue';
  return 'error-color';
};

const IndexingDetails = (props: IIndexHeaderDetails) => {
  const {ecaIndexData} = props;
  const statusClassName = getStatusClassName(ecaIndexData?.status);
  return (
    <>
      <div className="indexing-containers  index-header-details">
        <div className="index-details">
          <div className="index-properties">
            <div>
              <Typography className={`typo-text label2_size custom-color-eca`}>
                {t('indexes.createdBy')}
              </Typography>
              <Typography className="typo-text subhead3_size light-blue">
                {ecaIndexData?.createdByUser}
              </Typography>
            </div>
            <div className="docs-count">
              <Typography className={`typo-text label2_size custom-color-eca`}>
                {t('indexes.docsInIndex')}
              </Typography>
              <Typography className="typo-text subhead3_size light-blue">
                {ecaIndexData?.hasOwnProperty('totalDocCount') ? (
                  formatNumberWithComma(ecaIndexData?.totalDocCount)
                ) : (
                  <span style={{color: '#f2f4f7', marginTop: '0px'}}>
                    - - -
                  </span>
                )}
              </Typography>
            </div>
            {ecaIndexData?.type === IndexesType.standard ||
            ecaIndexData?.type === IndexesType.nlp ? (
              <div className="partially-filled">
                <Typography
                  className={`typo-text label2_size custom-color-eca`}
                >
                  {t('indexes.partiallyFailedDocsLabel')}
                </Typography>
                <Typography className="typo-text subhead3_size custom-dis-icon">
                  {ecaIndexData?.hasOwnProperty('partialFailedDocCount') ? (
                    <div className="count-container">
                      <Typography className="typo-text subhead3_size custom-dis-icon">
                        {formatNumberWithComma(
                          ecaIndexData?.partialFailedDocCount
                        )}
                      </Typography>
                      <AurTooltip
                        title={t(`indexes.partiallyFailedDocs`)}
                        className="typo-text label3_size"
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: 'offset',
                                options: {offset: [0, -6]},
                              },
                            ],
                          },
                        }}
                      >
                        <InfoIcon
                          className="info-icon-index"
                          style={{cursor: 'pointer', marginTop: '3px'}}
                          tabIndex={0}
                          aria-label={t('accessibility.indexManagement.moreInformation')}
                          aria-describedby={'partiallyFailedHint'}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                            }
                          }}
                        />
                      </AurTooltip>
                      <span id="partiallyFailedHint" style={{display: 'none'}}>
                        {t('indexes.partiallyFailedDocs')}
                      </span>
                    </div>
                  ) : (
                    <span style={{color: '#A0AEC2', marginTop: '0px'}}>
                      - - -
                    </span>
                  )}
                </Typography>
              </div>
            ) : null}
            {ecaIndexData?.type === IndexesType.standard ||
            ecaIndexData?.type === IndexesType.nlp ? (
              <div className="completely-fielled">
                <Typography
                  className={`typo-text label2_size custom-color-eca`}
                >
                  {t('indexes.completelyFailedDocsLabel')}
                </Typography>
                <Typography className="typo-text subhead3_size custom-dis-icon">
                  {ecaIndexData?.hasOwnProperty('totalFailedDocCount') ? (
                    <div className="count-container">
                      <Typography className="typo-text subhead3_size custom-dis-icon">
                        {formatNumberWithComma(
                          ecaIndexData?.totalFailedDocCount
                        )}
                      </Typography>
                      <AurTooltip
                        className="typo-text label3_size"
                        title={t(`indexes.completedFailedDocs`)}
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: 'offset',
                                options: {offset: [0, -6]},
                              },
                            ],
                          },
                        }}
                      >
                        <InfoIcon
                          className="info-icon-index"
                          style={{cursor: 'pointer', marginTop: '3px'}}
                          tabIndex={0}
                          aria-label={t('accessibility.indexManagement.moreInformation')}
                          aria-describedby={'completelyFailedHint'}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                            }
                          }}
                        />
                      </AurTooltip>
                      <span id="completelyFailedHint" style={{display: 'none'}}>
                        {t('indexes.completedFailedDocs')}
                      </span>
                    </div>
                  ) : (
                    <span style={{color: '#A0AEC2', marginTop: '0px'}}>
                      - - -
                    </span>
                  )}
                </Typography>
              </div>
            ) : null}
          </div>
        </div>
        <div className="status-container">
          <div className="status-details">
            <Typography
              className={`typo-text label2_size mist-color-700 custom-color-eca-status`}
            >
              {ecaIndexData?.status === indexingStatusKey.inProgress ? (
                'Current Status:'
              ) : shouldShowLastIndexingEvent(ecaIndexData?.status) ? (
                <>
                  {t('indexing.lastIndexingEvent')}
                  <AurLightTooltip title={t('common.yearMonthDate')}>
                    <span style={{marginLeft: 4}}>
                      {getUTCDateTimeForDisplay(ecaIndexData?.date)}
                    </span>
                  </AurLightTooltip>
                </>
              ) : null}
            </Typography>
            <Typography
              className={`typo-text subhead3_size light-blue-eca ${statusClassName}`}
            >
              {formatStatus(ecaIndexData?.status as Status)}
            </Typography>
          </div>
        </div>
      </div>
      <div className="index-history">
        <Typography className="typo-text subhead3_size mist-color-400">
          {t('indexes.indexHistory')}
        </Typography>
      </div>
    </>
  );
};

export default IndexingDetails;
